import React from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Container from "@mui/material/Container";
import theme from '../theme';
import AppLayout from "../components/AppLayout";
import AppSnackbarProvider from "../components/AppSnackbarProvider";
import { CacheProvider, EmotionCache } from '@emotion/react';
import createEmotionCache from '../createEmotionCache';
import type { AppProps } from 'next/app'
import Head from 'next/head';
import { useRouter } from 'next/router';
import { StoreProvider } from '@/stores';
import ConnectWalletModalProvider from "@/components/modals/WalletConnectModal/ConnectWalletModalProvider";
import "./tailwind.css";

const clientSideEmotionCache = createEmotionCache();

/**
 * This site duplicates swap.mayan.finance/explorer, so every page declares the matching page there
 * as its canonical url (error pages fall back to the explorer root).
 */
const CANONICAL_BASE = 'https://swap.mayan.finance/explorer';

function canonicalUrl(pathname: string, asPath: string): string {
  const path = asPath.split(/[?#]/)[0].replace(/\/+$/, '');
  // Error pages, and dynamic routes whose params are not resolved yet ("/trader/[trader]" on a
  // statically optimised render), point at the explorer root.
  if (pathname === '/404' || pathname === '/500' || path.includes('[')) {
    return CANONICAL_BASE;
  }
  return `${CANONICAL_BASE}${path}`;
}

interface MyAppProps extends AppProps {
  emotionCache?: EmotionCache;
}

export default function App({ Component, pageProps, emotionCache = clientSideEmotionCache }: MyAppProps) {
  const { pathname, asPath } = useRouter();

  return (
    <>
      <Head>
        <title>Cross-Chain Bridge Explorer | Mayan</title>
        <link rel="canonical" href={canonicalUrl(pathname, asPath)} key="canonical" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />
        <meta property="og:image" content="https://cdn.mayan.finance/explorer_preview.png" key="ogtagimage" />
        <meta property="twitter:image" content="https://cdn.mayan.finance/explorer_preview.png" key="twittertagimage" />
      </Head>
      <CacheProvider value={emotionCache}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
            <AppSnackbarProvider>
              <StoreProvider>
                <ConnectWalletModalProvider>
                  <Container></Container>
                  <AppLayout>
                    <Component {...pageProps} />
                  </AppLayout>
                </ConnectWalletModalProvider>
              </StoreProvider>
            </AppSnackbarProvider>
        </ThemeProvider>
      </CacheProvider>
    </>
  )
}
